import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  DollarSign, TrendingUp, TrendingDown, Trash2, 
  Calendar, FileText, ArrowUpCircle, ArrowDownCircle,
  BarChart3, PieChart, Camera, Loader2, CheckCircle, XCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const API = process.env.REACT_APP_BACKEND_URL;

const Accounting = () => {
  const { user, token } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState({ expense_categories: [], income_categories: [] });
  const [stats, setStats] = useState(null);
  const [dailyReport, setDailyReport] = useState(null);
  const [monthlyReport, setMonthlyReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showScanModal, setShowScanModal] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanResults, setScanResults] = useState(null);
  const [ticketImages, setTicketImages] = useState([]);
  const [transactionType, setTransactionType] = useState('ingreso');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  
  const [form, setForm] = useState({
    type: 'ingreso',
    amount: '',
    category: '',
    description: '',
    reference: ''
  });

  useEffect(() => {
    if (token && (user?.role === 'admin' || user?.role === 'cajera')) {
      loadData();
    }
  }, [token, user]);

  const loadData = async () => {
    try {
      const [txRes, catRes, statsRes, dailyRes, monthlyRes] = await Promise.all([
        axios.get(`${API}/accounting/transactions?limit=50`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/accounting/categories`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/accounting/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/accounting/daily-report?date=${selectedDate}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/accounting/monthly-report`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setTransactions(txRes.data);
      setCategories(catRes.data);
      setStats(statsRes.data);
      setDailyReport(dailyRes.data);
      setMonthlyReport(monthlyRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.amount || !form.category) {
      toast.error('Completa los campos obligatorios');
      return;
    }

    try {
      await axios.post(`${API}/accounting/transactions`, {
        type: form.type,
        amount: parseFloat(form.amount),
        category: form.category,
        description: form.description,
        reference: form.reference
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success(form.type === 'ingreso' ? 'Ingreso registrado' : 'Egreso registrado');
      setShowModal(false);
      setForm({ type: 'ingreso', amount: '', category: '', description: '', reference: '' });
      loadData();
    } catch (error) {
      toast.error('Error al registrar transacción');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta transacción?')) return;
    
    try {
      await axios.delete(`${API}/accounting/transactions/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Transacción eliminada');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const openModal = (type) => {
    setTransactionType(type);
    setForm({ ...form, type, category: '' });
    setShowModal(true);
  };

  // Handle ticket image selection
  const handleTicketImagesChange = (e) => {
    const files = Array.from(e.target.files);
    const imagePromises = files.map(file => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
    });
    
    Promise.all(imagePromises).then(images => {
      setTicketImages(prev => [...prev, ...images]);
    });
  };

  const removeTicketImage = (index) => {
    setTicketImages(prev => prev.filter((_, i) => i !== index));
  };

  const scanTickets = async () => {
    if (ticketImages.length === 0) {
      toast.error('Agrega al menos un ticket para escanear');
      return;
    }

    setScanning(true);
    setScanResults(null);

    try {
      const response = await axios.post(`${API}/accounting/scan-tickets`, {
        images: ticketImages
      }, { headers: { Authorization: `Bearer ${token}` } });

      setScanResults(response.data);
      toast.success(`${response.data.total_successful} tickets procesados correctamente`);
      loadData(); // Refresh data
    } catch (error) {
      toast.error('Error al escanear tickets');
      console.error(error);
    } finally {
      setScanning(false);
    }
  };

  const closeScanModal = () => {
    setShowScanModal(false);
    setTicketImages([]);
    setScanResults(null);
  };

  if (!user || (user.role !== 'admin' && user.role !== 'cajera')) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <DollarSign className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-700">Acceso Restringido</h2>
          <p className="text-slate-500 mt-2">Esta sección es solo para administradores y cajeras</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-800">Contabilidad</h1>
          <p className="text-slate-500">Gestiona ingresos y egresos de la farmacia</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Ingresos Hoy</p>
                <p className="text-2xl font-bold text-green-600">${stats?.today?.ingresos?.toFixed(2) || '0.00'}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                <TrendingDown className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Egresos Hoy</p>
                <p className="text-2xl font-bold text-red-600">${stats?.today?.egresos?.toFixed(2) || '0.00'}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Balance Hoy</p>
                <p className={`text-2xl font-bold ${(stats?.today?.balance || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  ${stats?.today?.balance?.toFixed(2) || '0.00'}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Balance Mes</p>
                <p className={`text-2xl font-bold ${(stats?.month?.balance || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  ${stats?.month?.balance?.toFixed(2) || '0.00'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-4 mb-8">
          <Button 
            onClick={() => openModal('ingreso')} 
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            <ArrowUpCircle className="w-4 h-4 mr-2" />
            Registrar Ingreso
          </Button>
          <Button 
            onClick={() => openModal('egreso')} 
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            <ArrowDownCircle className="w-4 h-4 mr-2" />
            Registrar Egreso
          </Button>
          <Button 
            onClick={() => setShowScanModal(true)} 
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Camera className="w-4 h-4 mr-2" />
            Escanear Tickets con IA
          </Button>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="transactions" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="transactions" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Transacciones
            </TabsTrigger>
            <TabsTrigger value="daily" className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Reporte Diario
            </TabsTrigger>
            <TabsTrigger value="monthly" className="flex items-center gap-2">
              <PieChart className="w-4 h-4" />
              Reporte Mensual
            </TabsTrigger>
          </TabsList>

          {/* Transactions Tab */}
          <TabsContent value="transactions">
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
              <div className="p-4 border-b bg-slate-50">
                <h3 className="font-semibold text-slate-700">Últimas Transacciones</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left p-4 text-sm font-medium text-slate-600">Fecha</th>
                      <th className="text-left p-4 text-sm font-medium text-slate-600">Tipo</th>
                      <th className="text-left p-4 text-sm font-medium text-slate-600">Categoría</th>
                      <th className="text-left p-4 text-sm font-medium text-slate-600">Descripción</th>
                      <th className="text-left p-4 text-sm font-medium text-slate-600">Monto</th>
                      <th className="text-left p-4 text-sm font-medium text-slate-600">Registrado por</th>
                      {user?.role === 'admin' && (
                        <th className="text-left p-4 text-sm font-medium text-slate-600">Acciones</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.length === 0 ? (
                      <tr>
                        <td colSpan={user?.role === 'admin' ? 7 : 6} className="p-8 text-center text-slate-500">
                          No hay transacciones registradas
                        </td>
                      </tr>
                    ) : (
                      transactions.map((tx) => (
                        <tr key={tx.id} className="border-t hover:bg-slate-50">
                          <td className="p-4 text-sm text-slate-600">
                            {new Date(tx.created_at).toLocaleDateString('es-MX', { 
                              day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                            })}
                          </td>
                          <td className="p-4">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                              tx.type === 'ingreso' 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-red-100 text-red-700'
                            }`}>
                              {tx.type === 'ingreso' ? (
                                <ArrowUpCircle className="w-3 h-3" />
                              ) : (
                                <ArrowDownCircle className="w-3 h-3" />
                              )}
                              {tx.type === 'ingreso' ? 'Ingreso' : 'Egreso'}
                            </span>
                          </td>
                          <td className="p-4 text-sm text-slate-700">{tx.category}</td>
                          <td className="p-4 text-sm text-slate-600">{tx.description || '-'}</td>
                          <td className={`p-4 text-sm font-semibold ${
                            tx.type === 'ingreso' ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {tx.type === 'ingreso' ? '+' : '-'}${tx.amount.toFixed(2)}
                          </td>
                          <td className="p-4 text-sm text-slate-600">{tx.created_by_name || 'Sistema'}</td>
                          {user?.role === 'admin' && (
                            <td className="p-4">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDelete(tx.id)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </td>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Daily Report Tab */}
          <TabsContent value="daily">
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-semibold text-slate-700">Reporte del Día</h3>
                <Input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => {
                    setSelectedDate(e.target.value);
                    // Reload daily report
                    axios.get(`${API}/accounting/daily-report?date=${e.target.value}`, {
                      headers: { Authorization: `Bearer ${token}` }
                    }).then(res => setDailyReport(res.data));
                  }}
                  className="w-48"
                />
              </div>
              
              {dailyReport && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-green-50 rounded-lg p-4">
                      <p className="text-sm text-green-600 mb-1">Total Ingresos</p>
                      <p className="text-2xl font-bold text-green-700">${dailyReport.total_ingresos.toFixed(2)}</p>
                    </div>
                    <div className="bg-red-50 rounded-lg p-4">
                      <p className="text-sm text-red-600 mb-1">Total Egresos</p>
                      <p className="text-2xl font-bold text-red-700">${dailyReport.total_egresos.toFixed(2)}</p>
                    </div>
                    <div className={`rounded-lg p-4 ${dailyReport.balance >= 0 ? 'bg-blue-50' : 'bg-orange-50'}`}>
                      <p className={`text-sm mb-1 ${dailyReport.balance >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>Balance del Día</p>
                      <p className={`text-2xl font-bold ${dailyReport.balance >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>
                        ${dailyReport.balance.toFixed(2)}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Ingresos por categoría */}
                    <div>
                      <h4 className="font-medium text-slate-700 mb-3">Ingresos por Categoría</h4>
                      {Object.keys(dailyReport.ingresos_by_category).length === 0 ? (
                        <p className="text-slate-500 text-sm">Sin ingresos registrados</p>
                      ) : (
                        <div className="space-y-2">
                          {Object.entries(dailyReport.ingresos_by_category).map(([cat, amount]) => (
                            <div key={cat} className="flex justify-between items-center bg-green-50 rounded-lg px-4 py-2">
                              <span className="text-sm text-slate-700">{cat}</span>
                              <span className="font-medium text-green-700">${amount.toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Egresos por categoría */}
                    <div>
                      <h4 className="font-medium text-slate-700 mb-3">Egresos por Categoría</h4>
                      {Object.keys(dailyReport.egresos_by_category).length === 0 ? (
                        <p className="text-slate-500 text-sm">Sin egresos registrados</p>
                      ) : (
                        <div className="space-y-2">
                          {Object.entries(dailyReport.egresos_by_category).map(([cat, amount]) => (
                            <div key={cat} className="flex justify-between items-center bg-red-50 rounded-lg px-4 py-2">
                              <span className="text-sm text-slate-700">{cat}</span>
                              <span className="font-medium text-red-700">${amount.toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Monthly Report Tab */}
          <TabsContent value="monthly">
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
              <h3 className="font-semibold text-slate-700 mb-6">
                Reporte Mensual - {monthlyReport ? `${monthlyReport.month}/${monthlyReport.year}` : ''}
              </h3>
              
              {monthlyReport && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-green-50 rounded-lg p-4">
                      <p className="text-sm text-green-600 mb-1">Total Ingresos</p>
                      <p className="text-2xl font-bold text-green-700">${monthlyReport.total_ingresos.toFixed(2)}</p>
                    </div>
                    <div className="bg-red-50 rounded-lg p-4">
                      <p className="text-sm text-red-600 mb-1">Total Egresos</p>
                      <p className="text-2xl font-bold text-red-700">${monthlyReport.total_egresos.toFixed(2)}</p>
                    </div>
                    <div className={`rounded-lg p-4 ${monthlyReport.balance >= 0 ? 'bg-blue-50' : 'bg-orange-50'}`}>
                      <p className={`text-sm mb-1 ${monthlyReport.balance >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>Balance</p>
                      <p className={`text-2xl font-bold ${monthlyReport.balance >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>
                        ${monthlyReport.balance.toFixed(2)}
                      </p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4">
                      <p className="text-sm text-purple-600 mb-1">Transacciones</p>
                      <p className="text-2xl font-bold text-purple-700">{monthlyReport.transactions_count}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Ingresos por categoría */}
                    <div>
                      <h4 className="font-medium text-slate-700 mb-3">Ingresos por Categoría</h4>
                      {Object.keys(monthlyReport.ingresos_by_category || {}).length === 0 ? (
                        <p className="text-slate-500 text-sm">Sin ingresos registrados</p>
                      ) : (
                        <div className="space-y-2">
                          {Object.entries(monthlyReport.ingresos_by_category || {}).map(([cat, amount]) => (
                            <div key={cat} className="flex justify-between items-center bg-green-50 rounded-lg px-4 py-2">
                              <span className="text-sm text-slate-700">{cat}</span>
                              <span className="font-medium text-green-700">${amount.toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Egresos por categoría */}
                    <div>
                      <h4 className="font-medium text-slate-700 mb-3">Egresos por Categoría</h4>
                      {Object.keys(monthlyReport.egresos_by_category || {}).length === 0 ? (
                        <p className="text-slate-500 text-sm">Sin egresos registrados</p>
                      ) : (
                        <div className="space-y-2">
                          {Object.entries(monthlyReport.egresos_by_category || {}).map(([cat, amount]) => (
                            <div key={cat} className="flex justify-between items-center bg-red-50 rounded-lg px-4 py-2">
                              <span className="text-sm text-slate-700">{cat}</span>
                              <span className="font-medium text-red-700">${amount.toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Transaction Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {form.type === 'ingreso' ? (
                  <>
                    <ArrowUpCircle className="w-5 h-5 text-green-600" />
                    Registrar Ingreso
                  </>
                ) : (
                  <>
                    <ArrowDownCircle className="w-5 h-5 text-red-600" />
                    Registrar Egreso
                  </>
                )}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Monto *</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    placeholder="0.00"
                    className="pl-8"
                    required
                  />
                </div>
              </div>

              <div>
                <Label>Categoría *</Label>
                <Select
                  value={form.category}
                  onValueChange={(value) => setForm({ ...form, category: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona categoría" />
                  </SelectTrigger>
                  <SelectContent>
                    {(form.type === 'ingreso' 
                      ? categories.income_categories 
                      : categories.expense_categories
                    ).map((cat) => (
                      <SelectItem key={cat.name} value={cat.name}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Descripción</Label>
                <Input
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Descripción opcional"
                />
              </div>

              <div>
                <Label>Referencia / Folio</Label>
                <Input
                  value={form.reference}
                  onChange={(e) => setForm({ ...form, reference: e.target.value })}
                  placeholder="Número de factura, recibo, etc."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowModal(false)}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className={`flex-1 ${form.type === 'ingreso' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
                >
                  Guardar
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Scan Tickets Modal */}
        <Dialog open={showScanModal} onOpenChange={closeScanModal}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Camera className="w-5 h-5 text-blue-600" />
                Escanear Tickets con IA
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Sube las fotos de los tickets de venta del día. La IA leerá automáticamente el monto y tipo de pago.
              </p>

              {/* Image Upload */}
              <div>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleTicketImagesChange}
                  className="hidden"
                  id="ticket-upload"
                  disabled={scanning}
                />
                <label
                  htmlFor="ticket-upload"
                  className={`flex items-center justify-center gap-2 w-full py-8 border-2 border-dashed rounded-xl transition-colors ${
                    scanning ? 'border-slate-200 bg-slate-50 cursor-not-allowed' : 'border-blue-300 hover:border-blue-500 hover:bg-blue-50 cursor-pointer'
                  }`}
                >
                  <Camera className="w-6 h-6 text-blue-500" />
                  <span className="text-blue-600 font-medium">
                    {scanning ? 'Procesando...' : 'Seleccionar fotos de tickets'}
                  </span>
                </label>
              </div>

              {/* Preview Images */}
              {ticketImages.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-2">
                    {ticketImages.length} ticket(s) seleccionado(s)
                  </p>
                  <div className="grid grid-cols-4 gap-2">
                    {ticketImages.map((img, idx) => (
                      <div key={idx} className="relative group">
                        <img
                          src={img}
                          alt={`Ticket ${idx + 1}`}
                          className="w-full h-20 object-cover rounded-lg"
                        />
                        <button
                          onClick={() => removeTicketImage(idx)}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          disabled={scanning}
                        >
                          <XCircle className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Scan Button */}
              {ticketImages.length > 0 && !scanResults && (
                <Button
                  onClick={scanTickets}
                  disabled={scanning}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  {scanning ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Escaneando con IA...
                    </>
                  ) : (
                    <>
                      <Camera className="w-4 h-4 mr-2" />
                      Escanear {ticketImages.length} Ticket(s)
                    </>
                  )}
                </Button>
              )}

              {/* Results */}
              {scanResults && (
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                    <h4 className="font-semibold text-green-800 mb-2">Resumen</h4>
                    <p className="text-sm text-green-700">{scanResults.summary}</p>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-blue-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-blue-700">{scanResults.total_processed}</p>
                      <p className="text-xs text-blue-600">Procesados</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-green-700">{scanResults.total_successful}</p>
                      <p className="text-xs text-green-600">Exitosos</p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-purple-700">${scanResults.total_amount.toFixed(2)}</p>
                      <p className="text-xs text-purple-600">Total</p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <h4 className="font-medium text-slate-700">Detalle por ticket:</h4>
                    {scanResults.results.map((result, idx) => (
                      <div
                        key={idx}
                        className={`flex items-center justify-between p-3 rounded-lg ${
                          result.success ? 'bg-green-50' : 'bg-red-50'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          {result.success ? (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-600" />
                          )}
                          <span className="text-sm">
                            {result.success
                              ? `Ticket ${result.ticket_number || idx + 1} - ${result.payment_method}`
                              : result.error}
                          </span>
                        </div>
                        {result.success && (
                          <span className="font-semibold text-green-700">
                            ${result.total?.toFixed(2)}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>

                  <Button
                    onClick={closeScanModal}
                    className="w-full"
                  >
                    Cerrar
                  </Button>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Accounting;
